/**
 * Web Push registration helpers (browser side).
 *
 * IMPORTANT: `Notification.requestPermission()` must be called from a USER
 * GESTURE (a click) — Safari/Firefox require it and Chrome silently downgrades
 * an automatic request to a tiny address-bar icon (no modal). So the prompt
 * lives in enablePush() (wired to a button); initPush() only registers the SW
 * and subscribes when permission was ALREADY granted (safe to call on login).
 *
 * Best-effort only: every function swallows its own errors (console.warn).
 */
import { getVapidPublicKey, savePushSubscription, removePushSubscription } from '../services/api';

// Convert a VAPID base64url public key into the Uint8Array PushManager wants.
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// Was this subscription made with the server's current VAPID public key? A
// browser that does not expose options.applicationServerKey returns true (no
// evidence either way); the subscribe() retry above covers that case.
function sameServerKey(sub, wanted) {
  const have = sub && sub.options && sub.options.applicationServerKey;
  if (!have) return true;
  const a = new Uint8Array(have);
  if (a.length !== wanted.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== wanted[i]) return false;
  return true;
}

// Whether this browser can do web push, and the current permission state.
export function getPushState() {
  const supported =
    'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  return { supported, permission: supported ? Notification.permission : 'unsupported' };
}

// Subscribe this device to push and hand the subscription to the backend.
// Assumes permission is already granted.
async function subscribeAndSave(reg) {
  const { key, enabled } = await getVapidPublicKey();
  if (!enabled || !key) return;
  const wanted = urlBase64ToUint8Array(key);
  let sub = await reg.pushManager.getSubscription();
  // SELF-HEAL ON A SERVER KEY CHANGE. 2026-10-08: cedgegroup.com moved servers
  // and the new server had NEW VAPID keys; every device kept the subscription it
  // had made under the OLD key, this code re-saved it as-is, and the push
  // service rejected every send with 403 "VAPID credentials ... do not
  // correspond" — phone alerts silently stopped for the whole team. A
  // subscription made with a different applicationServerKey can never be
  // delivered by this server, so drop it on both sides and subscribe afresh.
  if (sub && !sameServerKey(sub, wanted)) {
    const stale = sub.endpoint;
    try { await removePushSubscription(stale); } catch (e) {}
    try { await sub.unsubscribe(); } catch (e) {}
    sub = null;
  }
  if (!sub) {
    try {
      sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: wanted });
    } catch (e) {
      // Chrome refuses a second key while an old-key subscription still exists
      // ("A subscription with a different applicationServerKey ... already
      // exists") — reached only when options.applicationServerKey was not
      // readable above. Clear whatever is there and retry exactly once.
      const existing = await reg.pushManager.getSubscription();
      if (!existing) throw e;
      try { await removePushSubscription(existing.endpoint); } catch (e2) {}
      await existing.unsubscribe();
      sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: wanted });
    }
  }
  const j = sub.toJSON();
  await savePushSubscription({
    endpoint: j.endpoint,
    keys: j.keys,
    device_label: navigator.userAgent.slice(0, 120),
  });
}

// Register the service worker and — ONLY if permission is already granted —
// (re)subscribe. NEVER prompts, so it's safe to call automatically on login.
export async function initPush() {
  const { supported, permission } = getPushState();
  if (!supported) return;
  try {
    const reg = await navigator.serviceWorker.register('/sw.js');
    if (permission !== 'granted') return; // prompting must come from a gesture
    await subscribeAndSave(reg);
  } catch (e) {
    console.warn('initPush failed:', e);
  }
}

// GESTURE handler — call directly from a click. Prompts for permission (the
// requestPermission call is first so the user activation is fresh), then
// subscribes. Returns 'granted' | 'denied' | 'default' | 'unsupported'.
export async function enablePush() {
  const { supported } = getPushState();
  if (!supported) return 'unsupported';
  try {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return permission;
    const reg = await navigator.serviceWorker.register('/sw.js');
    await subscribeAndSave(reg);
    return 'granted';
  } catch (e) {
    console.warn('enablePush failed:', e);
    return 'denied';
  }
}

// Best-effort teardown: drop the subscription server-side, then unsubscribe.
export async function unregisterPush() {
  try {
    if (!('serviceWorker' in navigator)) return;
    const reg = await navigator.serviceWorker.getRegistration();
    if (!reg) return;
    const sub = await reg.pushManager.getSubscription();
    if (!sub) return;
    await removePushSubscription(sub.endpoint);
    await sub.unsubscribe();
  } catch (e) {
    console.warn('unregisterPush failed:', e);
  }
}
